import React, { useCallback, useEffect, useRef, useState } from "react";
import i18next from "i18next";
import classNames from "classnames";
import { Feature } from "ol";
import { useDrawingContext } from "../../Contexts/DrawingContext";
import { Colors } from "./Colors";
import { ColorButton } from "./ColorButton";
import { DrawType } from "../enums";
import { Point } from "ol/geom";
import { useMapContext } from "../../Contexts/MapContext";
import "./SketchProps.scss";

export interface ISketchProps {
    feature: Feature;
    placeholder?: string;
    onBlur?: () => void;
}
export const SketchProps = ({ feature, placeholder, onBlur }: ISketchProps): JSX.Element => {
    const { saveDrawnFeatures } = useMapContext();
    const { onNoteChange, onDeleteFeature, selectedFeature, setSelectedFeature, colorsVisible, setColorsVisible } =
        useDrawingContext();
    const [note, setNote] = useState<string>(feature.get("DESCRIPTION"));
    const [hideMeasurements, setHideMeasurements] = useState<boolean>(feature.get("MEASUREMENTSHIDDEN"));
    const [showColors, setShowColors] = useState<boolean>(false);
    const [color, setColor] = useState<string>(feature.get("COLOR"));

    const inputRef = useRef<HTMLInputElement>();

    useEffect(() => {
        if (!inputRef.current) {
            return;
        }
        if (feature && selectedFeature && feature.get("TEMPID") === selectedFeature.get("TEMPID")) {
            setTimeout(() => {
                inputRef.current.focus();
                inputRef.current.select();
            }, 0);
        }
    }, [selectedFeature]);

    const onChange = (e) => {
        setNote(e.target.value);
        feature.set("DESCRIPTION", e.target.value);
        onNoteChange();
        saveDrawnFeatures();
    };

    const onToggleMeasurement = () => {
        const newState = !feature.get("MEASUREMENTSHIDDEN");
        feature.set("MEASUREMENTSHIDDEN", newState);
        setHideMeasurements(newState);
        saveDrawnFeatures();
    };

    const onExpandColors = useCallback(() => {
        setShowColors(!showColors);
        // this is hack, to rerender accordion item, when color submenu visibility changed
        const newState =
            colorsVisible === true
                ? "true"
                : colorsVisible === "true"
                ? true
                : colorsVisible === false
                ? undefined
                : colorsVisible === undefined
                ? false
                : false;
        //@ts-ignore
        setColorsVisible(newState);
    }, [showColors, colorsVisible]);

    const onColorChange = (color: any) => {
        setColor(color.hex);
        feature.set("COLOR", color.hex);
        saveDrawnFeatures();
    };

    const onKeyDown = (e) => {
        if (e.code === "Enter" || e.code === "NumpadEnter" || e.code === "Escape") {
            setSelectedFeature(null);
            inputRef.current && inputRef.current.blur();
        }
    };

    return (
        <div
            className={classNames("draw-note", {
                selected: selectedFeature && feature.get("TEMPID") === selectedFeature.get("TEMPID")
            })}
        >
            <div className="draw-params">
                <div className="measurements" onClick={() => setSelectedFeature(feature)}>
                    {feature.get("DRAWTYPE") === DrawType.PointText ? (
                        <div className="input-container">
                            <input
                                ref={inputRef}
                                type="text"
                                placeholder={placeholder || "Ievadiet nosaukumu"}
                                onChange={(e: React.ChangeEvent<HTMLInputElement>) => onChange(e)}
                                onFocus={() => setSelectedFeature(feature)}
                                value={note}
                                onKeyDown={onKeyDown}
                            />
                        </div>
                    ) : null}
                    {feature.get("DRAWTYPE") === DrawType.Point ? (
                        <div className="measurement">
                            <div className="label">LKS:</div>
                            <div>{getCoordinates(feature)}</div>
                        </div>
                    ) : null}
                    {feature.get("LENGTH") ? (
                        <div className="measurement">
                            <div className="label">{i18next.t("sketch.length") + ":"}</div>
                            {feature.get("LENGTH")}
                        </div>
                    ) : null}
                    {feature.get("AREA") ? (
                        <div className="measurement">
                            <div className="label">{i18next.t("sketch.area") + ":"}</div>
                            {feature.get("AREA")}
                        </div>
                    ) : null}
                </div>
                <div className="measurement-tools">
                    {feature.get("DRAWTYPE") !== DrawType.Point ? (
                        <div className="show-measurement button" onClick={() => onToggleMeasurement()}>
                            <span className={hideMeasurements ? "icon-geolite-hidden" : "icon-geolite-view"}></span>
                        </div>
                    ) : null}
                    <div className="draw-delete button" onClick={() => onDeleteFeature(feature)}>
                        <span className="icon-geolite-trash"></span>
                    </div>
                    <ColorButton onClick={onExpandColors} color={color} />
                </div>
            </div>
            {showColors ? <Colors onChange={onColorChange} /> : null}
        </div>
    );
};

const getCoordinates = (feature: Feature) => {
    const coordinates = (feature.getGeometry() as Point).getCoordinates();
    return Math.round(coordinates[0]) + ", " + Math.round(coordinates[1]);
};
